<template>
  <div class="video-chapter-row-container">
    <button
      type="button"
      class="video-chapter-row"
      :class="`video-chapter-row--${state}`"
      :aria-label="ariaLabel"
      @click="handleClick"
    >
      <span
        class="video-chapter-row__thumbnail"
        :class="{ 'video-chapter-row__thumbnail--fallback': isFallbackThumbnail }"
      >
        <span class="video-chapter-row__thumbnail-frame">
          <img
            class="video-chapter-row__thumbnail-image"
            :src="resolvedThumbnailUrl"
            alt=""
            @error="handleThumbnailError"
          />
        </span>
        <span v-if="chapterNumber != null" class="video-chapter-row__number" aria-hidden="true">
          {{ chapterNumber }}
        </span>
      </span>

      <span class="video-chapter-row__content">
        <span class="video-chapter-row__top-row">
          <span class="video-chapter-row__title-group">
            <span class="video-chapter-row__title">{{ title }}</span>
            <span v-if="startTimeLabel" class="video-chapter-row__time-pill">
              <span class="video-chapter-row__time-pill-play" aria-hidden="true">▶</span>
              <span class="video-chapter-row__time-pill-label">{{ startTimeLabel }}</span>
            </span>
          </span>
          <span class="video-chapter-row__chevron" aria-hidden="true">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path
                d="M6 3L11 8L6 13"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </span>
          <span v-if="editable" class="video-chapter-row__edit-spacer" aria-hidden="true"></span>
        </span>
        <span v-if="description" class="video-chapter-row__description">{{ description }}</span>
      </span>
    </button>
    <button
      v-if="editable"
      type="button"
      class="video-chapter-row__edit"
      :aria-label="editAriaLabel"
      @click.stop="handleEditClick"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d="M2.87604 18.1156C2.92193 17.7025 2.94488 17.4959 3.00738 17.3027C3.06284 17.1313 3.14195 16.9684 3.24253 16.8187C3.35603 16.6498 3.50299 16.5028 3.79691 16.2089L17 3C18.1046 1.89543 19.8954 1.89543 21 3C22.1046 4.10457 22.1046 5.89543 21 7L7.79691 20.2031C7.50299 20.497 7.35603 20.644 7.18718 20.7575C7.03751 20.858 6.87456 20.9372 6.70314 20.9926C6.50993 21.0551 6.30337 21.0781 5.89025 21.124L2.5 21.5L2.87604 18.1156Z"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";

// Static frame placeholder when thumbnailUrl is null/unset (or fails to load) — no play
// overlay on the thumbnail (play affordance is the purple time pill only). ER-11069: real
// generated frames are passed as thumbnailUrl once IQ/Cloudflare Stream capture is wired.
const fallbackThumbnail = require("@components/assets/video-chapter-row-thumbnail.svg");

const props = defineProps({
  /** Visual variant. Stable enum key — not a display string. */
  state: {
    type: String,
    default: "default",
    validator: (value) => ["default", "hover", "active"].includes(value),
  },
  /** 1-indexed position in the chapter list. */
  chapterNumber: {
    type: Number,
    default: null,
  },
  /** Chapter title, from the generated segment. */
  title: {
    type: String,
    default: "",
  },
  /** Seek target in seconds. Drives the click-to-seek behavior. */
  startTimeSeconds: {
    type: Number,
    default: null,
  },
  /** Formatted display value, e.g. "1:00". */
  startTimeLabel: {
    type: String,
    default: "",
  },
  /** Short chapter description, from the generated segment. */
  description: {
    type: String,
    default: "",
  },
  /** Real chapter-frame thumbnail. null/unset falls back to the representative art. */
  thumbnailUrl: {
    type: String,
    default: null,
  },
  /** Shows the edit pencil in the title line. Learner views leave this off. */
  editable: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["seek", "edit"]);

const thumbnailFailed = ref(false);

const resolvedThumbnailUrl = computed(() => {
  if (props.thumbnailUrl && !thumbnailFailed.value) return props.thumbnailUrl;
  return fallbackThumbnail;
});

const isFallbackThumbnail = computed(() => !props.thumbnailUrl || thumbnailFailed.value);

const ariaLabel = computed(() => {
  const parts = [];
  if (typeof props.chapterNumber === "number") parts.push(`Play chapter ${props.chapterNumber}`);
  if (props.title) parts.push(props.title);
  if (props.startTimeLabel) parts.push(`starts at ${props.startTimeLabel}`);
  return parts.join(", ");
});

const editAriaLabel = computed(() =>
  typeof props.chapterNumber === "number" ? `Edit chapter ${props.chapterNumber}` : "Edit chapter"
);

function handleEditClick() {
  emit("edit");
}

function handleClick() {
  if (typeof props.startTimeSeconds === "number") {
    emit("seek", props.startTimeSeconds);
  }
}

function handleThumbnailError() {
  if (props.thumbnailUrl) thumbnailFailed.value = true;
}
</script>

<style scoped>
.video-chapter-row-container {
  position: relative;
  width: 100%;
}

.video-chapter-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  box-sizing: border-box;
  padding: 14px 18px;
  border: none;
  border-radius: 8px;
  background: #ffffff;
  color: #101828;
  font-family: Inter, sans-serif;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.video-chapter-row:hover,
.video-chapter-row--hover,
.video-chapter-row--active {
  background-color: #f9fafb;
}

.video-chapter-row:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}

.video-chapter-row__thumbnail {
  position: relative;
  flex-shrink: 0;
  width: 44px;
  height: 44px;
}

.video-chapter-row__thumbnail-frame {
  display: block;
  width: 44px;
  height: 44px;
  border-radius: 8px;
  overflow: hidden;
  background: #eaecf0;
}

.video-chapter-row__thumbnail--fallback .video-chapter-row__thumbnail-frame {
  background: #d0d5dd;
}

.video-chapter-row__thumbnail-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.video-chapter-row__number {
  position: absolute;
  left: -6px;
  top: -6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 1px solid #eaecf0;
  background: #ffffff;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  color: #667085;
  box-sizing: border-box;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.06);
}

.video-chapter-row__content {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.video-chapter-row__top-row {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
}

.video-chapter-row__title-group {
  display: flex;
  flex: 1;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.video-chapter-row__title {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13.5px;
  font-weight: 600;
  line-height: normal;
}

.video-chapter-row__description {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12.5px;
  font-weight: 400;
  line-height: normal;
  color: #667085;
}

.video-chapter-row__time-pill {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 6px;
  border-radius: 5px;
  background: #f4ebff;
  font-size: 11px;
  font-weight: 600;
  line-height: normal;
  color: #6941c6;
  white-space: nowrap;
}

.video-chapter-row__time-pill-play {
  font-size: 8px;
  line-height: 1;
}

.video-chapter-row__chevron {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #6941c6;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.video-chapter-row__edit-spacer {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
}

/* The pencil is a sibling of the row button (no nested buttons), overlaid on the title line.
   Hosts that change the row padding set --video-chapter-row-pad-x / -pad-y to match. */
.video-chapter-row__edit {
  position: absolute;
  top: calc(var(--video-chapter-row-pad-y, 14px) - 4px);
  right: var(--video-chapter-row-pad-x, 18px);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #667085;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.video-chapter-row__edit:hover {
  background-color: #eaecf0;
  color: #475467;
}

.video-chapter-row__edit:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}

.video-chapter-row:hover .video-chapter-row__chevron,
.video-chapter-row--hover .video-chapter-row__chevron,
.video-chapter-row--active .video-chapter-row__chevron {
  opacity: 1;
}
</style>
